import { Check, X } from 'lucide-react';
import { useMemo } from 'react';
import { PASSWORD_VALIDATION_RULES } from '@/utility/passwordValidationRules';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';

interface PasswordValidationIndicatorProps {
  password: string;
  confirmPassword?: string;
  showMatchValidation?: boolean;
  className?: string;
}

export function PasswordValidationIndicator({
  password,
  confirmPassword,
  showMatchValidation = false,
  className = '',
}: PasswordValidationIndicatorProps) {
  const { t } = useTranslation(TranslationNS.validations);
  const validationResults = useMemo(() => {
    const results = PASSWORD_VALIDATION_RULES.map((rule) => ({
      ...rule,
      label: t(`password_rules.${rule.id}`),
      isValid: rule.test(password),
    }));

    if (showMatchValidation && confirmPassword !== undefined) {
      results.push({
        id: 'match',
        label: t('password_must_match'),
        test: () => true,
        isValid: password === confirmPassword && password.length > 0,
      });
    }

    return results;
  }, [password, confirmPassword, showMatchValidation, t]);

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${className}`}
    >
      <p className="mb-3 text-xs font-medium text-gray-700">
        {t('password_requirements')}:
      </p>
      <ul className="grid gap-y-2 gap-x-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {validationResults.map((rule) => (
          <li
            key={rule.id}
            className="flex items-center gap-1.5 text-xs transition-colors"
          >
            <span
              className={`flex h-3.5 w-3.5 items-center justify-center rounded-full ${
                rule.isValid
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}
            >
              {rule.isValid ? (
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              ) : (
                <X className="h-2.5 w-2.5" strokeWidth={3} />
              )}
            </span>
            <span
              className={`${rule.isValid ? 'text-green-700' : 'text-red-600'}`}
            >
              {rule.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
